import { Component } from '@theme/component';

const CAR_W = 100;
const CAR_H = 46;
const RACE_RED = '#E10600';

/**
 * Race-day add-to-cart flight.
 * The cart request is unchanged — this element is only the visual.
 * prefers-reduced-motion removes it immediately so the add stays instant.
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean | string} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  /** @type {SVGSVGElement | null} */
  #trail = null;

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.remove();
      return;
    }

    if (!this.source?.isConnected || !this.destination?.isConnected) {
      this.remove();
      return;
    }

    try {
      this.#race();
    } catch {
      this.#trail?.remove();
      this.remove();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#trail?.remove();
    this.#trail = null;
  }

  #race() {
    const image = this.style.backgroundImage;
    this.#neutralizeHost();
    this.#buildCar(image);

    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();
    const start = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const end = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };
    const control = racingControl(start, end);
    const distance = Math.hypot(end.x - start.x, end.y - start.y);
    const duration = Math.round(Math.min(800, Math.max(600, 520 + distance * 0.22)));

    const frames = raceFrames(start, control, end);
    this.style.transform = frames[0].transform;
    this.style.opacity = '0';

    this.style.setProperty('--gl-race-ms', `${duration}ms`);
    this.#trail = createTrail(start, control, end, duration);

    const motion = this.animate(frames, {
      duration,
      easing: 'linear',
      fill: 'forwards',
    });

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      const icon = this.destination?.isConnected
        ? this.destination
        : document.querySelector('.header-actions__cart-icon');
      spawnFlag(end);
      popBadge(icon);
      this.#trail?.remove();
      this.#trail = null;
      if (this.isConnected) this.remove();
    };

    motion.finished.then(finish, finish);
    window.setTimeout(finish, duration + 420);
  }

  #neutralizeHost() {
    const locked = {
      animation: 'none',
      translate: 'none',
      background: 'none',
      'background-image': 'none',
      'background-color': 'transparent',
      overflow: 'visible',
      'border-radius': '0',
      width: `${CAR_W}px`,
      height: `${CAR_H}px`,
      'transform-origin': 'center center',
      'pointer-events': 'none',
      'z-index': '2147483646',
      margin: '0',
      padding: '0',
    };

    for (const [property, value] of Object.entries(locked)) {
      this.style.setProperty(property, value, 'important');
    }

    this.classList.add('gl-race-live');
    this.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {string} image
   */
  #buildCar(image) {
    const lines = document.createElement('span');
    lines.className = 'gl-race__lines';
    lines.innerHTML = '<span></span><span></span><span></span>';

    const car = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    car.setAttribute('class', 'gl-race__car');
    car.setAttribute('viewBox', `0 0 ${CAR_W} ${CAR_H}`);
    car.setAttribute('width', String(CAR_W));
    car.setAttribute('height', String(CAR_H));
    car.setAttribute('aria-hidden', 'true');
    car.innerHTML = CAR_MARKUP;

    this.append(lines, car);

    if (image && image !== 'none') {
      const thumb = document.createElement('span');
      thumb.className = 'gl-race__thumb';
      thumb.style.backgroundImage = image;
      this.append(thumb);
    }
  }
}

/**
 * @param {{x: number, y: number}} start
 * @param {{x: number, y: number}} end
 */
function racingControl(start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  let nx = -dy / len;
  let ny = dx / len;
  if (ny > 0) {
    nx = -nx;
    ny = -ny;
  }
  const bow = Math.max(36, Math.min(120, len * 0.2));
  return {
    x: (start.x + end.x) / 2 + nx * bow,
    y: (start.y + end.y) / 2 + ny * bow,
  };
}

/**
 * Fast launch with a short settle, sampled onto linear keyframe offsets.
 * @param {number} t
 */
function easeLaunch(t) {
  const settle = 1 - (1 - t) * (1 - t);
  return t * 0.78 + settle * 0.22;
}

/**
 * @param {{x: number, y: number}} p0
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {number} t
 */
function quadPoint(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
}

/**
 * @param {{x: number, y: number}} p0
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {number} t
 */
function quadTangent(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: 2 * u * (p1.x - p0.x) + 2 * t * (p2.x - p1.x),
    y: 2 * u * (p1.y - p0.y) + 2 * t * (p2.y - p1.y),
  };
}

/**
 * @param {{x: number, y: number}} start
 * @param {{x: number, y: number}} control
 * @param {{x: number, y: number}} end
 */
function raceFrames(start, control, end) {
  const steps = 20;
  /** @type {Keyframe[]} */
  const frames = [];
  let angle = 0;

  for (let i = 0; i < steps; i += 1) {
    const linearT = i / (steps - 1);
    const t = easeLaunch(linearT);
    const point = quadPoint(start, control, end, t);
    const tangent = quadTangent(start, control, end, t);
    if (Math.hypot(tangent.x, tangent.y) > 0.001) {
      angle = (Math.atan2(tangent.y, tangent.x) * 180) / Math.PI;
    }

    let opacity = 1;
    if (linearT < 0.07) opacity = linearT / 0.07;
    else if (linearT > 0.9) opacity = Math.max(0, (1 - linearT) / 0.1);

    frames.push({
      transform: `translate(${point.x - CAR_W / 2}px, ${point.y - CAR_H / 2}px) rotate(${angle}deg)`,
      opacity,
      offset: linearT,
    });
  }

  return frames;
}

/**
 * @param {{x: number, y: number}} start
 * @param {{x: number, y: number}} control
 * @param {{x: number, y: number}} end
 * @param {number} duration
 */
function createTrail(start, control, end, duration) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'gl-race-trail');
  svg.setAttribute('aria-hidden', 'true');
  const width = window.innerWidth;
  const height = window.innerHeight;
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', `M ${start.x} ${start.y} Q ${control.x} ${control.y} ${end.x} ${end.y}`);
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', RACE_RED);
  path.setAttribute('stroke-width', '2');
  path.setAttribute('stroke-linecap', 'round');
  svg.append(path);
  document.body.append(svg);

  const length = path.getTotalLength();
  path.style.strokeDasharray = `${length}`;
  const steps = 20;
  /** @type {Keyframe[]} */
  const frames = [];
  for (let i = 0; i < steps; i += 1) {
    const linearT = i / (steps - 1);
    frames.push({
      strokeDashoffset: length * (1 - easeLaunch(linearT)),
      offset: linearT,
    });
  }
  path.animate(frames, { duration, easing: 'linear', fill: 'forwards' });
  svg.animate(
    [
      { opacity: 0.55, offset: 0 },
      { opacity: 0.4, offset: 0.72 },
      { opacity: 0, offset: 1 },
    ],
    { duration, easing: 'linear', fill: 'forwards' }
  );

  return svg;
}

/**
 * @param {{x: number, y: number}} point
 */
function spawnFlag(point) {
  const flag = document.createElement('div');
  flag.className = 'gl-race-flag';
  flag.setAttribute('aria-hidden', 'true');
  flag.style.left = `${point.x}px`;
  flag.style.top = `${point.y}px`;
  flag.innerHTML = `${FLAG_MARKUP}<i style="--dx:-16px;--dy:-12px"></i><i style="--dx:18px;--dy:-10px"></i><i style="--dx:-12px;--dy:14px"></i><i style="--dx:14px;--dy:16px"></i><i style="--dx:0px;--dy:-20px"></i><i style="--dx:22px;--dy:2px"></i>`;
  document.body.append(flag);
  flag.addEventListener('animationend', () => flag.remove(), { once: true });
  window.setTimeout(() => flag.remove(), 700);
}

/**
 * @param {Element | null | undefined} icon
 */
function popBadge(icon) {
  const root = icon instanceof Element ? icon : null;
  if (!root) return;

  const play = () => {
    const bubble = document.querySelector('.header-actions__cart-icon .cart-bubble');
    if (!bubble) return;
    bubble.classList.remove('gl-race-badge-pop');
    window.requestAnimationFrame(() => bubble.classList.add('gl-race-badge-pop'));
  };

  play();

  const parent = root.parentElement;
  if (!parent) return;
  const observer = new MutationObserver(() => {
    observer.disconnect();
    play();
  });
  observer.observe(parent, { subtree: true, childList: true, characterData: true });
  window.setTimeout(() => observer.disconnect(), 900);
}

const CAR_MARKUP = `
  <rect x="1" y="8.5" width="18" height="3" rx="0.6" fill="${RACE_RED}"/>
  <rect x="1" y="13.2" width="18" height="3" rx="0.6" fill="${RACE_RED}"/>
  <rect x="14.2" y="5.6" width="2.6" height="15.2" fill="${RACE_RED}"/>
  <path fill="${RACE_RED}" d="M22 30.5c1.4-9.2 10-14.2 22-15.2 6.2-.5 12 .4 16.4 3.4l12.2 4.2c5.6 2 10.2 5.2 13.2 8.4H70.2c-2.2-2.2-6.4-4-11.4-4.6l-11.2-1.6c-3.2-.4-6.2.6-8.2 2.8l-6.4 2.6c-2.2.8-4.8.4-6.6-1H22z"/>
  <path fill="${RACE_RED}" d="M24 22.5h46c2 0 3.2 1.6 2.4 3.4l-4.6 8.2H28.2l-6.2-8.4c-.8-1.6.4-3.2 2-3.2z"/>
  <path fill="${RACE_RED}" d="M68 20.5c8.4 1.2 16.8 4.6 22.4 8.2l6.2 1.4c1.6.4 1.4 2.6-.2 2.8L74 31.2c-4.2-3.2-10.6-6.2-16.8-7.4L68 20.5z"/>
  <path fill="#0E0E10" d="M44 14.8c5.2-.4 10.6 1.2 13.2 4.4 1 1.2.2 3-1.4 3.2-5.2.6-10.8-.2-14.6-2.4-1.6-1-1.8-2.8-.4-3.8 1-.6 2-.9 3.2-1z"/>
  <path fill="none" stroke="#F4F4F5" stroke-width="1.2" stroke-linecap="round" d="M47.5 15.2c4-2.4 9.6-2.2 13 1.4"/>
  <rect x="86" y="30.2" width="12" height="2.4" rx="0.4" fill="${RACE_RED}"/>
  <rect x="94.2" y="27.2" width="2.2" height="8" fill="${RACE_RED}"/>
  <circle cx="32" cy="34" r="8.2" fill="#0E0E10"/>
  <circle cx="32" cy="34" r="5.2" fill="none" stroke="${RACE_RED}" stroke-width="1.3"/>
  <circle cx="32" cy="34" r="2.4" fill="${RACE_RED}"/>
  <circle cx="78" cy="34" r="7.2" fill="#0E0E10"/>
  <circle cx="78" cy="34" r="4.6" fill="none" stroke="${RACE_RED}" stroke-width="1.2"/>
  <circle cx="78" cy="34" r="2.1" fill="${RACE_RED}"/>
`;

const FLAG_MARKUP = `
  <svg class="gl-race-flag__svg" viewBox="0 0 36 36" width="36" height="36" aria-hidden="true">
    <path d="M7 3.5v29" stroke="#F4F4F5" stroke-width="1.7" stroke-linecap="round"/>
    <g>
      <rect x="8" y="4" width="5.2" height="5.2" fill="#0E0E10" stroke="#F4F4F5" stroke-width="0.6"/>
      <rect x="13.2" y="4" width="5.2" height="5.2" fill="#F4F4F5"/>
      <rect x="18.4" y="4" width="5.2" height="5.2" fill="#0E0E10" stroke="#F4F4F5" stroke-width="0.6"/>
      <rect x="23.6" y="4" width="5.2" height="5.2" fill="#F4F4F5"/>
      <rect x="8" y="9.2" width="5.2" height="5.2" fill="#F4F4F5"/>
      <rect x="13.2" y="9.2" width="5.2" height="5.2" fill="#0E0E10" stroke="#F4F4F5" stroke-width="0.6"/>
      <rect x="18.4" y="9.2" width="5.2" height="5.2" fill="#F4F4F5"/>
      <rect x="23.6" y="9.2" width="5.2" height="5.2" fill="#0E0E10" stroke="#F4F4F5" stroke-width="0.6"/>
      <rect x="8" y="14.4" width="5.2" height="5.2" fill="#0E0E10" stroke="#F4F4F5" stroke-width="0.6"/>
      <rect x="13.2" y="14.4" width="5.2" height="5.2" fill="#F4F4F5"/>
      <rect x="18.4" y="14.4" width="5.2" height="5.2" fill="#0E0E10" stroke="#F4F4F5" stroke-width="0.6"/>
      <rect x="23.6" y="14.4" width="5.2" height="5.2" fill="#F4F4F5"/>
    </g>
  </svg>
`;

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
